import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
} from 'recharts'
import AppDropdown from '../../../components/AppDropdown'
import { AppTooltip, ChartTooltipSurface } from '../../../components/AppTooltip'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import DashboardRightDrawer from '../shared/components/DashboardRightDrawer'
import {
  useGetCompanyOptionsQuery,
  useGetFinancialDashboardQuery,
  useLazyGetFinancialDashboardDrilldownQuery,
  useGetProjectOptionsQuery,
} from '../../../api/serverStateHooks'
import { AppSkeleton, WidgetSkeleton, ChartSkeleton, TableSkeleton } from '../../../components/skeleton'

// ── Date helpers ──────────────────────────────────────────────────────────────

function toISO(d) {
  const y = d.getFullYear()
  const m = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${day}`
}

function startOfWeek(d) {
  const date = new Date(d)
  const dow = date.getDay()
  const diff = dow === 0 ? -6 : 1 - dow // Monday as first day
  date.setDate(date.getDate() + diff)
  return date
}

function endOfWeek(d) {
  const mon = startOfWeek(d)
  const sun = new Date(mon)
  sun.setDate(mon.getDate() + 6)
  return sun
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

function startOfYear(d) {
  return new Date(d.getFullYear(), 0, 1)
}

function endOfYear(d) {
  return new Date(d.getFullYear(), 11, 31)
}

function shiftPeriod(anchor, mode, delta) {
  const d = new Date(anchor)
  if (mode === 'weekly') {
    d.setDate(d.getDate() + delta * 7)
  } else if (mode === 'monthly') {
    d.setMonth(d.getMonth() + delta)
  } else {
    d.setFullYear(d.getFullYear() + delta)
  }
  return d
}

function getPeriodRange(anchor, mode) {
  const d = new Date(anchor)
  if (mode === 'weekly') return { from: startOfWeek(d), to: endOfWeek(d) }
  if (mode === 'monthly') return { from: startOfMonth(d), to: endOfMonth(d) }
  return { from: startOfYear(d), to: endOfYear(d) }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function periodLabel(anchor, mode) {
  const d = new Date(anchor)
  if (mode === 'weekly') {
    const { from, to } = getPeriodRange(d, 'weekly')
    const fromLabel = `${from.getDate()} ${MONTHS[from.getMonth()]}`
    const toLabel = `${to.getDate()} ${MONTHS[to.getMonth()]} ${to.getFullYear()}`
    return `${fromLabel} – ${toLabel}`
  }
  if (mode === 'monthly') {
    return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
  }
  return `${d.getFullYear()}`
}

// ── Misc helpers ──────────────────────────────────────────────────────────────

function toNumber(value) {
  const parsed = Number(value || 0)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatCurrencyRaw(value, symbol = '₹') {
  const n = toNumber(value)
  if (n >= 1_00_00_000) return `${symbol}${(n / 1_00_00_000).toFixed(2)} Cr`
  if (n >= 1_00_000) return `${symbol}${(n / 1_00_000).toFixed(2)} L`
  if (n >= 1000) return `${symbol}${(n / 1000).toFixed(1)} K`
  return `${symbol}${n.toFixed(2)}`
}

function sortProfitabilityRows(rows, mode) {
  const copy = [...rows]
  switch (mode) {
    case 'margin_asc':
      copy.sort((a, b) => toNumber(a.margin) - toNumber(b.margin))
      return copy
    case 'margin_desc':
      copy.sort((a, b) => toNumber(b.margin) - toNumber(a.margin))
      return copy
    case 'received_desc':
      copy.sort((a, b) => toNumber(b.receivedAmount) - toNumber(a.receivedAmount))
      return copy
    case 'cost_desc':
      copy.sort((a, b) => toNumber(b.cost) - toNumber(a.cost))
      return copy
    case 'budget_desc':
    default:
      copy.sort((a, b) => toNumber(b.budget) - toNumber(a.budget))
      return copy
  }
}

// ── Period Navigator ──────────────────────────────────────────────────────────

const PERIOD_MODES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
]

function PeriodNavigator({ mode, onModeChange, label, onPrev, onNext, isCurrentPeriod, onToday }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl bg-surface-container-low p-2">
      {/* Mode toggle */}
      <div className="flex rounded-xl bg-surface-container p-1 gap-0.5">
        {PERIOD_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => onModeChange(m.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${mode === m.value
              ? 'bg-primary text-on-primary shadow-sm'
              : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-low'
              }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Arrow navigation */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrev}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-all"
          title="Previous period"
        >
          <span className="material-symbols-outlined text-base">chevron_left</span>
        </button>

        <button
          type="button"
          onClick={onToday}
          className={`min-w-[190px] px-3 py-1.5 rounded-lg text-sm font-semibold text-center transition-all ${isCurrentPeriod
            ? 'text-primary font-bold'
            : 'text-on-surface hover:bg-surface-container'
            }`}
          title="Jump to current period"
        >
          {label}
        </button>

        <button
          type="button"
          onClick={onNext}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-all"
          title="Next period"
        >
          <span className="material-symbols-outlined text-base">chevron_right</span>
        </button>
      </div>

      {/* Today shortcut - only when not on current period */}
      {!isCurrentPeriod && (
        <button
          type="button"
          onClick={onToday}
          className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider text-primary hover:bg-primary/10 transition-all"
        >
          Today
        </button>
      )}
    </div>
  )
}

// ── Tooltip ───────────────────────────────────────────────────────────────────

function FinancialTrendTooltip({ active, payload, label, symbol }) {
  if (!active || !payload?.length) return null
  const data = payload[0]?.payload || {}
  const sym = symbol || '₹'
  return (
    <ChartTooltipSurface className="text-xs font-medium text-on-surface">
      <p className="mb-0.5 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">{label}</p>
      {data.date ? (
        <p className="mb-2 text-[10px] text-on-surface-variant/70">{data.date}</p>
      ) : null}
      <div className="flex items-center justify-between gap-5">
        <span className="inline-flex items-center gap-2">
          <i className="h-2.5 w-2.5 rounded-full bg-primary" />
          Expected Inflow
        </span>
        <strong>{formatCurrencyRaw(data.expectedInflow, sym)}</strong>
      </div>
      <div className="flex items-center justify-between gap-5">
        <span className="inline-flex items-center gap-2">
          <i className="h-2.5 w-2.5 rounded-full bg-green-600" />
          Inflow
        </span>
        <strong>{formatCurrencyRaw(data.inflow, sym)}</strong>
      </div>
      <div className="flex items-center justify-between gap-5">
        <span className="inline-flex items-center gap-2">
          <i className="h-2.5 w-2.5 rounded-full bg-secondary" />
          Outflow
        </span>
        <strong>{formatCurrencyRaw(data.outflow, sym)}</strong>
      </div>
      {toNumber(data.inflow) > 0 && toNumber(data.outflow) > 0 && (
        <div className={`mt-1 flex items-center justify-between gap-5 border-t border-outline-variant/30 pt-1 ${toNumber(data.inflow) >= toNumber(data.outflow) ? 'text-primary' : 'text-error'}`}>
          <span>Net</span>
          <strong>{formatCurrencyRaw(toNumber(data.inflow) - toNumber(data.outflow), sym)}</strong>
        </div>
      )}
    </ChartTooltipSurface>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────────

export default function FinancialDashboardScreen() {
  const navigate = useNavigate()
  const today = useMemo(() => new Date(), [])

  // Period state: mode + anchor date (any date inside the target period)
  const [periodMode, setPeriodMode] = useState('weekly')
  const [anchor, setAnchor] = useState(today)

  const [companyId, setCompanyId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [isProfitabilityOpen, setIsProfitabilityOpen] = useState(false)
  const [profitabilitySort, setProfitabilitySort] = useState('budget_desc')
  const [profitabilityPage, setProfitabilityPage] = useState(1)
  const [drill, setDrill] = useState({ open: false, level: '', title: '', rows: [], columns: [], total: 0, page: 1, pageSize: 25, breadcrumb: [], dimensions: {}, loading: false, error: '', formula: '' })
  const profitabilityPageSize = 8
  const [loadFinancialDrill] = useLazyGetFinancialDashboardDrilldownQuery()

  useBodyScrollLock(isProfitabilityOpen || drill.open)

  // Compute from/to from anchor + mode
  const { from: periodFrom, to: periodTo } = useMemo(
    () => getPeriodRange(anchor, periodMode),
    [anchor, periodMode],
  )

  const isCurrentPeriod = useMemo(() => {
    const { from: cf, to: ct } = getPeriodRange(today, periodMode)
    return toISO(periodFrom) === toISO(cf) && toISO(periodTo) === toISO(ct)
  }, [periodFrom, periodTo, periodMode, today])

  const currentLabel = useMemo(() => periodLabel(anchor, periodMode), [anchor, periodMode])

  const queryParams = useMemo(
    () => ({
      company_id: companyId || undefined,
      project_id: projectId || undefined,
      from_date: toISO(periodFrom),
      to_date: toISO(periodTo),
      timeframe: periodMode,
    }),
    [companyId, projectId, periodFrom, periodTo, periodMode],
  )

  async function openDrill(level, dimensions = {}, page = 1) {
    const effectiveDimensions = Object.keys(dimensions).length ? dimensions : (drill.dimensions || {})
    setDrill((prev) => ({ ...prev, open: true, level, dimensions: effectiveDimensions, page, loading: true, error: '' }))
    try {
      const result = await loadFinancialDrill({ ...queryParams, ...effectiveDimensions, level, page, page_size: 25 }).unwrap()
      setDrill({ open: true, level: result.level || level, title: result.title || 'Financial details', rows: Array.isArray(result.rows) ? result.rows : [], columns: Array.isArray(result.columns) ? result.columns : [], total: Number(result.total || 0), page: Number(result.page || page), pageSize: Number(result.pageSize || 25), breadcrumb: Array.isArray(result.breadcrumb) ? result.breadcrumb : [], dimensions: effectiveDimensions, loading: false, error: '', formula: result.formula || result.calculationBasis || '' })
    } catch (error) {
      setDrill((prev) => ({ ...prev, open: true, loading: false, error: error?.data?.detail || 'Failed to load financial details.' }))
    }
  }

  async function exportDrill(format) {
    const params = new URLSearchParams(Object.entries({ ...queryParams, ...drill.dimensions, level: drill.level, format }).filter(([, value]) => value !== undefined && value !== null && value !== ''))
    const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'}/dashboard/financial/export?${params.toString()}`, { headers: { authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } })
    if (!response.ok) throw new Error('Export failed')
    const filename = (response.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/i)?.[1] || `financial-drill.${format}`
    const url = URL.createObjectURL(await response.blob()); const anchor = document.createElement('a'); anchor.href = url; anchor.download = filename; document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url)
  }

  const { data, isLoading, isError } = useGetFinancialDashboardQuery(queryParams)
  const { data: companies = [] } = useGetCompanyOptionsQuery({ page: 1, page_size: 200 })
  const { data: projects = [] } = useGetProjectOptionsQuery({ page: 1, page_size: 200 })

  useEffect(() => {
    if (!companyId) return
    const isValid = companies.some((item) => String(item.id) === String(companyId))
    if (!isValid) {
      setCompanyId('')
      setProjectId('')
    }
  }, [companyId, companies])

  useEffect(() => {
    if (!projectId) return
    const isValid = projects.some((item) => String(item.id) === String(projectId))
    if (!isValid) setProjectId('')
  }, [projectId, projects])

  const companyOptions = useMemo(
    () => [
      { value: '', label: 'All Companies' },
      ...companies.map((c) => ({ value: String(c.id), label: c.name })),
    ],
    [companies],
  )

  const filteredProjects = companyId
    ? projects.filter((item) => String(item.companyId || '') === String(companyId))
    : projects

  const projectOptions = useMemo(
    () => [
      { value: '', label: 'All Projects' },
      ...filteredProjects.map((p) => ({ value: String(p.id), label: p.name })),
    ],
    [filteredProjects],
  )

  // Period navigator handlers
  const handlePrev = () => setAnchor((prev) => shiftPeriod(prev, periodMode, -1))
  const handleNext = () => setAnchor((prev) => shiftPeriod(prev, periodMode, 1))
  const handleToday = () => setAnchor(new Date())
  const handleModeChange = (newMode) => {
    setPeriodMode(newMode)
    setAnchor(new Date()) // reset to current period on mode change
  }

  // Cash flow chart
  const symbol = data?.filtersMeta?.baseCurrencySymbol || '₹'
  const cashPoints = data?.weeklyCashFlow?.points || []
  const hasWeeklyCashFlow = useMemo(
    () => cashPoints.some((point) => toNumber(point?.expectedInflow) > 0 || toNumber(point?.inflow) > 0 || toNumber(point?.outflow) > 0),
    [cashPoints],
  )

  // Profitability drawer
  const profitabilityAllRows = data?.projectProfitability?.allItems || []
  const sortedProfitabilityRows = useMemo(
    () => sortProfitabilityRows(profitabilityAllRows, profitabilitySort),
    [profitabilityAllRows, profitabilitySort],
  )
  const profitabilityTotalPages = Math.max(1, Math.ceil(sortedProfitabilityRows.length / profitabilityPageSize))
  const normalizedProfitabilityPage = Math.min(profitabilityPage, profitabilityTotalPages)
  const drawerRows = useMemo(() => {
    const start = (normalizedProfitabilityPage - 1) * profitabilityPageSize
    return sortedProfitabilityRows.slice(start, start + profitabilityPageSize)
  }, [normalizedProfitabilityPage, sortedProfitabilityRows])

  // Cashflow period title label (dynamic based on mode)
  const cashFlowSectionTitle = {
    weekly: 'Weekly Cash Flow',
    monthly: 'Monthly Cash Flow',
    yearly: 'Yearly Cash Flow',
  }[periodMode] || 'Cash Flow'

  if (isError && !data) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center p-20 text-error">
        <span className="material-symbols-outlined mb-2 text-5xl">error</span>
        <p className="font-bold">Failed to load financial dashboard data.</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="mx-auto w-full max-w-[1600px] pb-14 space-y-8 animate-fade-in">
        {/* Header Skeleton */}
        <section className="flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-end">
          <div className="space-y-2">
            <AppSkeleton width="340px" height="48px" className="rounded-xl" />
            <AppSkeleton width="420px" height="20px" className="rounded-lg" />
          </div>
          <div className="flex gap-3">
            <AppSkeleton width="170px" height="42px" className="rounded-md" />
            <AppSkeleton width="170px" height="42px" className="rounded-md" />
          </div>
        </section>

        {/* Navigator Skeleton */}
        <section className="flex items-center justify-between gap-4">
          <AppSkeleton width="280px" height="38px" className="rounded-lg" />
        </section>

        {/* Charts & KPI Stats */}
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <ChartSkeleton height="340px" title="Cash Flow Projection" subtitle="Inflow vs Outflow overview" />
          </div>
          <div className="col-span-12 grid grid-cols-1 gap-6 sm:grid-cols-3 lg:col-span-4 lg:grid-cols-1">
            <WidgetSkeleton lines={2} showHeader={true} className="min-h-[100px]" />
            <WidgetSkeleton lines={2} showHeader={true} className="min-h-[100px]" />
            <WidgetSkeleton lines={2} showHeader={true} className="min-h-[100px]" />
          </div>
        </section>

        {/* Tables Skeleton */}
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-6">
            <TableSkeleton rows={4} cols={3} title="Project Profitability" />
          </div>
          <div className="col-span-12 lg:col-span-6">
            <TableSkeleton rows={4} cols={3} title="AR / AP Aging Breakdown" />
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[1600px] pb-14">
      {/* Header */}
      <section className="mb-8 flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-6xl font-extrabold tracking-tight text-on-surface">Financial Dashboard</h1>
          <p className="mt-2 text-lg text-on-surface-variant">
            Company-wide liquidity and performance overview.
          </p>
        </div>


      </section>

      {/* Period Navigator */}
      <section className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <PeriodNavigator
          mode={periodMode}
          onModeChange={handleModeChange}
          label={currentLabel}
          onPrev={handlePrev}
          onNext={handleNext}
          isCurrentPeriod={isCurrentPeriod}
          onToday={handleToday}
        />
        {/* <p className="text-xs text-on-surface-variant">
          {toISO(periodFrom)} → {toISO(periodTo)}
        </p> */}

        {/* Filters row */}
        <div className="flex w-full flex-wrap items-end gap-3 lg:w-auto">
          {/* Company filter */}
          <label className="grid min-w-[170px] gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Company</span>
            <AppDropdown
              value={companyId}
              onChange={(nextCompanyId) => {
                setCompanyId(nextCompanyId)
                if (!projectId) return
                const selected = projects.find((item) => String(item.id) === String(projectId))
                if (selected && nextCompanyId && String(selected.companyId || '') !== String(nextCompanyId || '')) {
                  setProjectId('')
                }
              }}
              placeholder="All Companies"
              triggerClassName="rounded-md border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
              options={companyOptions}
            />
          </label>

          {/* Project filter */}
          <label className="grid min-w-[170px] gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Project</span>
            <AppDropdown
              value={projectId}
              onChange={setProjectId}
              placeholder="All Projects"
              triggerClassName="rounded-md border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
              options={projectOptions}
            />
          </label>
        </div>
      </section>

      <section className="grid grid-cols-12 gap-6">
        {/* Cash Flow Chart */}
        <article className="col-span-12 rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 lg:col-span-8">
          <div className="mb-8 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold text-on-surface">{cashFlowSectionTitle}</h2>
              <p className="text-sm text-on-surface-variant">
                Inflow vs Outflow — <span className="font-medium text-on-surface">{currentLabel}</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-4 text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                <span className="inline-flex items-center gap-2">
                  <i className="h-3 w-3 rounded-full bg-primary" /> Expected Inflow
                </span>
                <span className="inline-flex items-center gap-2">
                  <i className="h-3 w-3 rounded-full bg-green-600" /> Inflow
                </span>
                <span className="inline-flex items-center gap-2">
                  <i className="h-3 w-3 rounded-full bg-secondary" /> Outflow
                </span>
              </div>
              {data.weeklyCashFlow?.totals && (
                <div className="flex gap-4 text-xs font-bold">
                  <span className="text-green-600">{data.weeklyCashFlow.totals.inflowLabel}</span>
                  <span className="text-secondary">{data.weeklyCashFlow.totals.outflowLabel}</span>
                </div>
              )}
              <button type="button" onClick={() => openDrill('cash_flow')} className="text-xs font-bold text-primary hover:underline">View transactions</button>
            </div>
          </div>

          {/* Remove padding from wrapper; use LineChart margin instead so x-axis labels never clip */}
          <div className="relative h-64 rounded-2xl border border-outline-variant/20 bg-surface-container-low">
            <div className="h-full w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  accessibilityLayer
                  data={cashPoints}
                  onClick={() => openDrill('cash_flow')}
                  margin={{ left: 16, right: 20, top: 12, bottom: 20 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(67, 70, 83, 0.16)" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={10} interval={0} minTickGap={0} tick={{ fontSize: 11 }} />
                  <RechartsTooltip cursor={false} content={<FinancialTrendTooltip symbol={symbol} />} />
                  <Line
                    dataKey="expectedInflow"
                    type="monotone"
                    stroke="var(--primary)"
                    strokeWidth={2.5}
                    dot={false}
                    name="Expected Inflow"
                  />
                  <Line
                    dataKey="inflow"
                    type="monotone"
                    stroke="#16a34a"
                    strokeWidth={2.5}
                    dot={false}
                    name="Inflow"
                  />
                  <Line
                    dataKey="outflow"
                    type="monotone"
                    stroke="var(--secondary)"
                    strokeWidth={2.5}
                    dot={false}
                    name="Outflow"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {!hasWeeklyCashFlow ? (
              <div className="pointer-events-none absolute inset-0 grid place-items-center">
                <div className="rounded-full bg-surface-container-lowest/85 px-4 py-2 text-xs font-semibold text-on-surface-variant backdrop-blur-sm">
                  No cash flow data for this period.
                </div>
              </div>
            ) : null}
          </div>
          <p className="mt-2 text-[11px] text-on-surface-variant">X-axis: period date. Y-axis: amount in {data.filtersMeta?.baseCurrency || 'INR'}; select the chart or View transactions for source records.</p>
        </article>

        {/* Expected vs Received */}
        <article className="col-span-12 flex flex-col rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 lg:col-span-4">
          <h2 className="text-3xl font-bold text-on-surface">Expected vs Received</h2>
          <p className="mb-8 text-sm text-on-surface-variant">Collection health for selected range</p>

          <div className="space-y-6">
            <button type="button" onClick={() => openDrill('expected')} className="block w-full rounded-lg p-1 text-left hover:bg-surface-container-low">
              <div className="mb-2 flex justify-between text-xs font-bold uppercase tracking-widest text-on-surface-variant">
                <span>Expected</span>
                <span>{data.expectedVsReceived.expectedLabel}</span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-surface-container-low">
                <span className="block h-full w-full bg-primary-container" />
              </div>
            </button>

            <button type="button" onClick={() => openDrill('received')} className="block w-full rounded-lg p-1 text-left hover:bg-surface-container-low">
              <div className="mb-2 flex justify-between text-xs font-bold uppercase tracking-widest text-on-surface-variant">
                <span>Received</span>
                <span>{data.expectedVsReceived.receivedLabel}</span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-surface-container-low">
                <span
                  className="block h-full bg-primary"
                  style={{ width: `${Math.min(Math.max(data.expectedVsReceived.receivedPct, 0), 100)}%` }}
                />
              </div>
            </button>
          </div>

          <div className="mt-8 border-t border-outline-variant/30 pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-on-surface-variant">Outstanding</span>
              <button type="button" onClick={() => openDrill('outstanding')} className="text-2xl font-extrabold text-error hover:underline">{data.expectedVsReceived.outstandingLabel}</button>
            </div>
          </div>
        </article>

        {/* Project Profitability */}
        <article className="col-span-12 rounded-3xl bg-surface-container-low p-6 lg:col-span-7">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-3xl font-bold text-on-surface">Project Profitability</h2>
            <button
              type="button"
              onClick={() => {
                setProfitabilityPage(1)
                setIsProfitabilityOpen(true)
              }}
              className="text-xs font-bold uppercase tracking-widest text-primary hover:underline"
            >
              Full Report
            </button>
            <button type="button" onClick={() => openDrill('profitability')} className="text-xs font-bold uppercase tracking-widest text-primary hover:underline">View profit / loss details</button>
          </div>

          <div className="space-y-6">
            {data.projectProfitability.items.map((row) => {
              const budget = Math.max(0, toNumber(row.budget))
              const received = Math.max(0, toNumber(row.receivedAmount))
              const cost = Math.max(0, toNumber(row.cost))
              const receivedPct = budget > 0 ? Math.min(100, (received / budget) * 100) : 0
              const costPct = budget > 0 ? Math.min(100, (cost / budget) * 100) : 0
              const showExcess = budget > 0 && (received > budget || cost > budget)
              return (
                <div key={row.projectId} className="flex items-center gap-5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-surface-container-lowest text-sm font-bold text-primary shadow-sm">
                    {row.projectCode || row.projectId}
                  </div>

                  <div className="flex-1">
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <button type="button" onClick={() => openDrill('profitability', { project_id: row.projectId })} className="text-left text-sm font-bold text-primary hover:underline">{row.projectName}</button>
                      <span className={`text-sm font-bold ${row.isLoss ? 'text-error' : 'text-primary'}`}>
                        {row.statusLabel} ({row.marginLabel})
                      </span>
                    </div>
                    <AppTooltip
                      contentClassName="max-w-[380px] px-4 py-3 text-sm"
                      content={
                        <div className="space-y-1.5">
                          <p className="text-xs font-bold uppercase tracking-wider text-primary">
                            {row.projectCode || row.projectId} - {row.projectName}
                          </p>
                          <p>Budget: {row.budgetLabel}</p>
                          <p>Total Received: {row.receivedLabel}</p>
                          <p>Employee Cost: {row.costLabel}</p>
                          <p>Remaining: {row.remainingLabel}</p>
                          <p>Margin: {row.marginLabel}</p>
                          <p>Status: {row.statusLabel}</p>
                          {showExcess ? <p className="font-semibold text-error">Excess over budget detected</p> : null}
                        </div>
                      }
                    >
                      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-surface-variant">
                        <span className="absolute left-0 top-0 h-full bg-primary-container" style={{ width: `${receivedPct}%` }} />
                        <span className="absolute left-0 top-0 h-full bg-primary" style={{ width: `${costPct}%`, opacity: 0.85 }} />
                        {showExcess ? <span className="absolute right-0 top-0 h-full w-1.5 bg-error" /> : null}
                      </div>
                    </AppTooltip>
                    <div className="mt-1 flex items-center justify-between text-[11px] font-semibold text-on-surface-variant">
                      <span>Received {row.receivedLabel}</span>
                      <span>Cost {row.costLabel}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </article>

        {/* Upcoming Billing */}
        <article className="col-span-12 rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 lg:col-span-5">
          <h2 className="mb-5 text-3xl font-bold text-on-surface">Upcoming Billing</h2>

          <div className="divide-y divide-outline-variant/25">
            {data.upcomingBilling.items.map((item) => (
              <button type="button" key={item.projectId} onClick={() => openDrill(item.isUrgent ? 'overdue_billing' : 'upcoming_billing', { project_id: item.projectId })} className="flex w-full items-center justify-between rounded-lg py-4 text-left hover:bg-surface-container-low">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-container-low text-on-surface-variant">
                    <span className="material-symbols-outlined text-base">description</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-on-surface">{item.projectCode || item.projectName}</p>
                    <p className="text-[11px] uppercase tracking-wider text-on-surface-variant">
                      {item.companyName || item.customerName || 'Billing'}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-sm font-bold text-on-surface">{item.amountLabel}</p>
                  <p className="text-[11px] text-on-surface-variant">Remaining receivable</p>
                  <p className={`text-[11px] font-semibold ${item.isUrgent ? 'text-error' : 'text-on-surface-variant'}`}>
                    {item.dueLabel}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </article>

        {/* Billing Status */}
        <article className="col-span-12 rounded-3xl bg-surface-container-lowest p-8">
          <h2 className="mb-8 text-3xl font-bold text-on-surface">Project Billing Status</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            {data.projectBillingStatus.items.map((metric) => {
              const total = Math.max(data.filtersMeta.projectCount, 1)
              const width = `${Math.min((Number(metric.count || 0) / total) * 100, 100)}%`
              return (
                <button type="button" key={metric.status} onClick={() => openDrill(metric.status === 'OVERDUE' ? 'overdue_billing' : 'expected', { payment_status: metric.status })} className="w-full rounded-lg p-1 text-left hover:bg-surface-container-low">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">
                    {metric.status.replace('_', ' ')}
                  </p>
                  <div className="mt-3 flex items-end gap-2">
                    <span className="text-5xl font-extrabold text-on-surface">{metric.count}</span>
                    <span className="text-sm text-on-surface-variant">Projects</span>
                  </div>
                  <div className="mt-4 h-1 w-full rounded-full bg-surface-container-low">
                    <span className="block h-full rounded-full bg-primary" style={{ width }} />
                  </div>
                </button>
              )
            })}
          </div>
        </article>
      </section>

      {/* Full Profitability Drawer */}
      <DashboardRightDrawer
        open={isProfitabilityOpen}
        title="Full Profitability Report"
        subtitle={`${sortedProfitabilityRows.length} filtered projects`}
        onClose={() => setIsProfitabilityOpen(false)}
        page={normalizedProfitabilityPage}
        totalPages={profitabilityTotalPages}
        onPrevious={() => setProfitabilityPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setProfitabilityPage((prev) => Math.min(profitabilityTotalPages, prev + 1))}
        onPageChange={setProfitabilityPage}
        headerExtra={(
          <div className="flex max-w-[250px] flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Sort By</span>
            <AppDropdown
              value={profitabilitySort}
              onChange={(nextValue) => {
                setProfitabilitySort(String(nextValue))
                setProfitabilityPage(1)
              }}
              options={[
                { value: 'budget_desc', label: 'Budget (High to Low)' },
                { value: 'margin_asc', label: 'Margin (Worst First)' },
                { value: 'margin_desc', label: 'Margin (Best First)' },
                { value: 'received_desc', label: 'Received (High to Low)' },
                { value: 'cost_desc', label: 'Cost (High to Low)' },
              ]}
            />
          </div>
        )}
      >
        <div className="overflow-x-auto rounded-2xl border border-outline-variant/20">
          <table className="min-w-[860px] w-full border-collapse text-left">
            <thead className="bg-surface-container-low">
              <tr>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Project</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Code</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant text-right">Budget</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant text-right">Received</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant text-right">Cost</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant text-right">Remaining</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant text-right">Margin</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20 bg-surface-container-lowest">
              {drawerRows.map((row) => (
                <tr key={row.projectId}>
                  <td className="px-4 py-3 text-sm font-semibold text-on-surface">{row.projectName}</td>
                  <td className="px-4 py-3 text-sm text-on-surface-variant">{row.projectCode || row.projectId}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-on-surface">{row.budgetLabel}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-on-surface">{row.receivedLabel}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-on-surface">{row.costLabel}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-on-surface">{row.remainingLabel}</td>
                  <td className={`px-4 py-3 text-sm text-right font-semibold ${row.isLoss ? 'text-error' : 'text-primary'}`}>{row.marginLabel}</td>
                  <td className={`px-4 py-3 text-xs font-bold uppercase tracking-wider ${row.isLoss ? 'text-error' : 'text-primary'}`}>{row.statusLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardRightDrawer>

      <DashboardRightDrawer
        open={drill.open}
        title={drill.title || 'Financial details'}
        subtitle={(drill.breadcrumb || []).join(' › ')}
        onClose={() => setDrill((prev) => ({ ...prev, open: false }))}
        widthClassName="max-w-[900px]"
        page={drill.page}
        totalPages={Math.max(1, Math.ceil(Number(drill.total || 0) / Math.max(1, Number(drill.pageSize || 25))))}
        onPrevious={() => openDrill(drill.level, drill.dimensions, Math.max(1, drill.page - 1))}
        onNext={() => openDrill(drill.level, drill.dimensions, drill.page + 1)}
        onPageChange={(page) => openDrill(drill.level, drill.dimensions, Number(page))}
        headerExtra={<div className="space-y-2"><div className="flex flex-wrap gap-2"><button type="button" onClick={() => setDrill((prev) => ({ ...prev, open: false }))} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Back</button><button type="button" onClick={() => setDrill((prev) => ({ ...prev, open: false }))} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Up one level</button><button type="button" onClick={() => setDrill((prev) => ({ ...prev, open: false, dimensions: {} }))} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Reset drilldown</button><button type="button" onClick={() => exportDrill('csv')} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">CSV</button><button type="button" onClick={() => exportDrill('pdf')} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">PDF</button></div><p className="text-xs text-on-surface-variant">Current period, company and project filters are retained. {drill.formula}</p></div>}
      >
        {drill.loading ? <p className="py-10 text-center text-sm text-on-surface-variant">Loading details...</p> : null}
        {drill.error ? <p className="rounded-lg bg-error-container p-3 text-sm text-error">{drill.error}</p> : null}
        {!drill.loading && !drill.error && !drill.rows.length ? <p className="py-10 text-center text-sm text-on-surface-variant">No records match the selected criteria.</p> : null}
        {!drill.loading && !drill.error && drill.rows.length ? <div className="overflow-x-auto"><table className="min-w-full text-left text-xs"><thead className="bg-surface-container-low"><tr>{drill.columns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2 font-bold text-on-surface-variant">{column.label}</th>)}<th className="px-3 py-2 font-bold text-on-surface-variant">Source</th></tr></thead><tbody className="divide-y divide-outline-variant/15">{drill.rows.map((row, index) => { const path = row.timesheetId ? `/nerve/timesheet/entry?id=${row.timesheetId}&mode=view` : row.projectId ? `/nerve/project/detail?id=${row.projectId}` : null; return <tr key={row.receiptId || row.timesheetId || row.projectId || `${row.date || 'row'}-${index}`}>{drill.columns.map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2 text-on-surface">{typeof row[column.key] === 'number' && /amount|cost|profit|margin/i.test(column.key) ? formatCurrencyRaw(row[column.key], symbol) : row[column.key] ?? '—'}</td>)}<td className="px-3 py-2">{path ? <button type="button" onClick={() => navigate(path, { state: { returnTo: window.location.pathname + window.location.search } })} className="font-bold text-primary hover:underline">View source</button> : <span className="text-on-surface-variant">In drawer</span>}</td></tr> })}</tbody></table></div> : null}
      </DashboardRightDrawer>
    </div>
  )
}
